import { type Validator, disallowHtml } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  FieldLabel,
  TextField,
  Columns,
  Column,
  Dropdown,
  Stack,
  FieldMessage,
} from 'braid-design-system';
import {
  type CountryCode,
  getExampleNumber,
  parsePhoneNumber,
  validatePhoneNumberLength,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import React, { useEffect, useRef, useState } from 'react';

import { useConfig } from 'src/shared/hooks';

import { useField } from '../../EnquiryForm';

import translations from './.vocab';
import { allCountryCodes, seekCountryCodes } from './areaCodeData';

interface Props {
  id: string;
  label?: string;
}

export const ContactNumber = ({ id, label = 'Contact Number' }: Props) => {
  const [callingCode, setCallingCode] = useState('');
  const [localError, setLocalError] = useState('');
  const { t } = useTranslations(translations);
  const { locale } = useConfig();

  const formattedList = [
    { label: 'SEEK countries', data: seekCountryCodes },
    { label: 'All countries', data: allCountryCodes },
  ];

  const countryCode = allCountryCodes.find(
    (item) => item.callingCode === callingCode,
  )?.countryCode as CountryCode;

  const callingCodeRef = useRef('');
  const invalidNumberMessage = t('Invalid $countryCode based phone number.', {
    countryCode,
  });

  const isE164: Validator<string> = {
    test: ({ value }: { value: string }) => {
      if (value) {
        try {
          const phoneNumber = parsePhoneNumber(
            value.replace(/\s+/g, ''),
            countryCode,
          );
          if (phoneNumber) {
            return phoneNumber.isValid();
          }
          return false;
        } catch (e) {
          console.log(e); // eslint-disable-line no-console
          return false;
        }
      }
      return true;
    },
    getErrorMessage: () => Promise.resolve(invalidNumberMessage),
    getErrorMessageSync: () => invalidNumberMessage,
    name: 'isE164',
  };
  const { errorMessage, onChange, value } = useField({
    id: 'telephone',
    validators: [disallowHtml, isE164] as any,
  });

  const getPlaceholder = () => {
    if (countryCode) {
      return (
        getExampleNumber(countryCode, examples)
          ?.formatInternational()
          ?.replace(`+${callingCode} `, '')
          ?.replace(/\s+/g, '') || ''
      );
    }
    return t('Enter phone number');
  };

  useEffect(() => {
    if (
      value &&
      callingCodeRef.current &&
      callingCodeRef.current !== callingCode
    ) {
      onChange(value.replace(`+${callingCodeRef.current}`, `+${callingCode}`));
    }
  }, [callingCode, onChange, value]);

  useEffect(() => {
    setCallingCode(
      allCountryCodes.find((item) => item.countryCode === locale)
        ?.callingCode || '',
    );
  }, [locale]);

  return (
    <Box id={id}>
      <Stack space="xxsmall">
        <FieldLabel
          label={label}
          secondaryLabel={t('optional')}
          htmlFor="area-code"
        />
        <Columns
          space={['small', 'medium']}
          alignY="center"
          collapseBelow="tablet"
        >
          <Column width="2/5">
            <Dropdown
              id="area-code"
              label={undefined}
              secondaryLabel={t('optional')}
              onChange={(e) => {
                const newAreaCode = (e.target as HTMLSelectElement).value;
                callingCodeRef.current = callingCode;
                setCallingCode(newAreaCode);
              }}
              value={callingCode}
              placeholder={t('Select country code')}
            >
              {formattedList.map((item) => (
                <optgroup label={item.label} key={item.label}>
                  {item.data.map((area, index) => (
                    <option key={index} value={area.callingCode}>
                      {area.countryName} ( +{area.callingCode} )
                    </option>
                  ))}
                </optgroup>
              ))}
            </Dropdown>
          </Column>
          <Column>
            <TextField
              disabled={!callingCode}
              id="contact-number"
              label={undefined}
              prefix={`+${callingCode}`}
              type="text"
              placeholder={getPlaceholder()}
              value={
                value
                  ? value
                      .replace(`+${callingCode}`, '')
                      .replace(`+${callingCodeRef}`, '')
                  : ''
              }
              onChange={(e) => {
                const enteredValue = e.currentTarget.value;
                if (!enteredValue) {
                  onChange('');
                  return;
                }

                if (
                  Boolean(enteredValue.match(/\s\s+/g)) ||
                  !enteredValue.match(/^[0-9\s]*$/)
                ) {
                  // just numbers, no consecutive space
                  return;
                }
                const pNumber = enteredValue
                  ? `+${callingCode}${enteredValue}`
                  : '';
                if (
                  validatePhoneNumberLength(
                    pNumber.replace(/\s+/g, ''),
                    countryCode,
                  ) === 'NOT_A_NUMBER'
                ) {
                  setLocalError('Not a valid number');
                  return;
                }
                if (
                  validatePhoneNumberLength(
                    pNumber.replace(/\s+/g, ''),
                    countryCode,
                  ) === 'TOO_LONG'
                ) {
                  return;
                }
                setLocalError('');
                onChange(pNumber);
              }}
              onClear={() => onChange('')}
              tone={errorMessage ? 'critical' : 'neutral'}
            />
          </Column>
        </Columns>
        {errorMessage || localError ? (
          <Box paddingY="xxsmall">
            <FieldMessage
              id="error-message"
              message={localError || errorMessage}
              tone="critical"
            />
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};
